import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Link,
} from '@mui/material';
import { useAccount, useWaitForTransaction, useContractWrite, useContractRead } from 'wagmi';
import { parseUnits } from 'viem';
import { CONTRACTS, CHAIN_ID, getTxUrl, isDynamicFeesEnabled, getLexiTokenAddress } from '../config/contracts';
import { LexiCoreV2ABI } from '../abis/LexiCoreV2';
import { ERC20ABI } from '../abis';
import { useChatFee } from '../hooks/useDynamicFees';
import '../styles/retro-terminal.css';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'lexi';
  timestamp: Date;
  confidence?: number;
  domainsQueried?: string[];
  txHash?: string;
  gasUsed?: bigint;
}

// Generate intelligent LEXI responses based on the question
const generateLexiResponse = (question: string): string => {
  const lowerQuestion = question.toLowerCase();
  
  if (lowerQuestion.includes('who are you') || lowerQuestion.includes('what are you')) {
    return `🤖 **I am LEXI - Your Autonomous Linguistic Agent**\n\nI'm a fully on-chain AI oracle system deployed on Base. I have comprehensive knowledge across multiple domains and I'm part of the Living Arcade artistic ecosystem.\n\n**Transaction:** Recorded on-chain with 95% confidence`;
  }
  
  if (lowerQuestion.includes('living arcade')) {
    return `🎨 **Living Arcade - My Artistic Home**\n\nLiving Arcade is the creative ecosystem that birthed me. It's a decentralized art project exploring code as living art, community-driven creation, and blockchain as canvas.\n\n**Transaction:** Recorded on-chain with 95% confidence`;
  }
  
  return `🤖 **LEXI Response**\n\nI understand your question: "${question}"\n\nI'm still learning to provide comprehensive answers to all questions. My knowledge is expanding through community contributions.\n\n**Transaction:** Recorded on-chain with 85% confidence`;
};

// Loading component while Wagmi initializes
function LoadingOracle() {
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          border: '1px solid #0f0'
        }}
      >
        <CircularProgress sx={{ color: '#0f0' }} />
        <Typography sx={{ mt: 2, color: '#0f0' }}>Initializing LEXI Oracle...</Typography>
        <Typography variant="caption" sx={{ mt: 1, color: '#0f0', opacity: 0.7 }}>
          Connecting to Base Mainnet (Chain ID: 8453)...
        </Typography>
      </Paper>
    </Container>
  );
}

// Main Oracle component - only renders when Wagmi is ready
function OracleInterfaceInner() {
  const { isConnected, address } = useAccount();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load conversation history from localStorage on mount
  useEffect(() => {
    if (address) {
      const storageKey = `lexi_conversation_${address.toLowerCase()}`;
      const savedMessages = localStorage.getItem(storageKey);
      if (savedMessages) {
        try {
          const parsed = JSON.parse(savedMessages);
          // Convert timestamp strings back to Date objects and BigInt strings back to BigInt
          const messagesWithDates = parsed.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
            gasUsed: msg.gasUsed ? BigInt(msg.gasUsed) : undefined,
          }));
          setMessages(messagesWithDates);
          console.log('✅ Loaded conversation history:', messagesWithDates.length, 'messages');
        } catch (error) {
          console.error('Error loading conversation history:', error);
        }
      }
    }
  }, [address]);

  // Save conversation history to localStorage whenever messages change
  useEffect(() => {
    if (address && messages.length > 0) {
      const storageKey = `lexi_conversation_${address.toLowerCase()}`;
      try {
        // Convert BigInt values to strings for JSON serialization
        const messagesToSave = messages.map(msg => ({
          ...msg,
          gasUsed: msg.gasUsed ? msg.gasUsed.toString() : undefined,
          timestamp: msg.timestamp.toISOString(),
        }));
        localStorage.setItem(storageKey, JSON.stringify(messagesToSave));
      } catch (error) {
        console.error('Error saving conversation history:', error);
      }
    }
  }, [messages, address]);

  // ⚠️ PRODUCTION: Using Base Mainnet (Chain ID 8453)
  const effectiveChainId = CHAIN_ID.BASE_MAINNET; // 8453
  const contracts = CONTRACTS[effectiveChainId];
  const isDynamic = isDynamicFeesEnabled(effectiveChainId);
  const { fee: chatFee } = useChatFee();
  const lexiTokenAddress = getLexiTokenAddress(effectiveChainId);
  
  console.log('🟢 Oracle Interface using Base Mainnet');
  console.log('🟢 Chain ID:', effectiveChainId);
  console.log('🟢 LEXI_CORE:', contracts.LEXI_CORE);
  console.log('🟢 Dynamic fees:', isDynamic);
  console.log('🟢 Chat fee:', chatFee, 'LEXI');

  // Check current allowance
  const { data: currentAllowance } = useContractRead({
    address: lexiTokenAddress as `0x${string}`,
    abi: ERC20ABI,
    functionName: 'allowance',
    args: address && contracts.LEXI_CORE ? [address, contracts.LEXI_CORE] : undefined,
    query: {
      enabled: !!address && !!contracts.LEXI_CORE && isDynamic && chatFee && parseFloat(chatFee) > 0,
    },
  });

  // Token approval hook for LEXI tokens
  const { 
    writeAsync: approveAsync,
    data: approveHash,
  } = useContractWrite({
    address: lexiTokenAddress as `0x${string}`,
    abi: ERC20ABI,
    functionName: 'approve',
  });

  useWaitForTransaction({
    hash: approveHash as `0x${string}` | undefined,
    enabled: !!approveHash,
  });

  // Wagmi v1 contract write hook for askLexi
  const { 
    writeAsync: writeContractAsync,
    data: hash,
    isLoading: isPending,
    error: writeError 
  } = useContractWrite({
    address: contracts.LEXI_CORE,
    abi: LexiCoreV2ABI,
    functionName: 'askLexi',
  });
  
  // Convert hash to proper format for useWaitForTransaction
  const txHash = hash ? (typeof hash === 'string' ? hash : (hash as any)?.hash || String(hash)) : undefined;
  
  // Wait for transaction confirmation
  const { 
    data: receipt, 
    isLoading: isConfirming, 
    isSuccess: isConfirmed 
  } = useWaitForTransaction({
    hash: txHash as `0x${string}` | undefined,
    enabled: !!txHash,
  });

  const isTransactionComplete = isConfirmed && receipt && hash;

  // Debug logging for transaction status
  useEffect(() => {
    if (txHash) {
      console.log('🔍 Transaction hash:', txHash);
      console.log('🔍 Waiting for confirmation... isConfirming:', isConfirming, 'isConfirmed:', isConfirmed);
      if (receipt) {
        console.log('✅ Receipt received!', receipt);
      }
    }
  }, [txHash, isConfirming, isConfirmed, receipt]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle transaction success
  useEffect(() => {
    if (isTransactionComplete && receipt && currentQuestion) {
      console.log('✅ Transaction confirmed!');
      console.log('📝 Receipt:', receipt);
      console.log('❓ Question:', currentQuestion);
      
      // Try to extract response from logs or use generated response
      let lexiResponse = generateLexiResponse(currentQuestion);
      
      // Check if receipt has logs with response data
      if (receipt.logs && receipt.logs.length > 0) {
        console.log('📋 Transaction logs:', receipt.logs);
      }
      
      // Convert hash to string for display
      let hashStr: string = '';
      if (hash) {
        if (typeof hash === 'string') {
          hashStr = hash;
        } else if (hash && typeof hash === 'object' && 'hash' in hash) {
          hashStr = (hash as any).hash;
        } else if (hash && typeof hash === 'object' && 'transactionHash' in hash) {
          hashStr = (hash as any).transactionHash;
        } else {
          hashStr = String(hash);
        }
        hashStr = hashStr.replace(/^["']|["']$/g, '');
      }
      
      setMessages(prev => [...prev, {
        id: (Date.now() + 2).toString(),
        text: lexiResponse,
        sender: 'lexi',
        timestamp: new Date(),
        txHash: hashStr,
        gasUsed: receipt.gasUsed,
      }]);
      setIsLoading(false);
      setCurrentQuestion('');
    }
  }, [isTransactionComplete, receipt, currentQuestion, hash]);

  // Handle transaction hash - log to console only
  useEffect(() => {
    if (hash) {
      // Convert hash to string properly - handle both string and object types
      let hashStr: string;
      if (typeof hash === 'string') {
        hashStr = hash;
      } else if (hash && typeof hash === 'object' && 'hash' in hash) {
        hashStr = (hash as any).hash;
      } else if (hash && typeof hash === 'object' && 'transactionHash' in hash) {
        hashStr = (hash as any).transactionHash;
      } else {
        hashStr = JSON.stringify(hash);
      }
      
      // Clean up the hash string (remove quotes if any)
      hashStr = hashStr.replace(/^["']|["']$/g, '');
      
      // Log to console instead of adding to chat
      console.log('📝 Transaction submitted!');
      console.log('🔗 Transaction Hash:', hashStr);
      console.log('🔗 View on BaseScan:', getTxUrl(effectiveChainId, hashStr));
      console.log('⏳ Waiting for confirmation...');
    }
  }, [hash, effectiveChainId]);

  // Handle transaction error - log technical details to console, show user-friendly message in chat
  useEffect(() => {
    if (writeError) {
      const errorMessage = writeError.message || 'Failed to query LEXI';
      
      // Log full error to console
      console.error('❌ Transaction error:', writeError);
      console.error('Error message:', errorMessage);
      
      // Determine user-friendly message
      let userMessage = 'I encountered an error processing your request.';
      
      if (errorMessage.includes('User rejected') || errorMessage.includes('user rejected')) {
        userMessage = 'You cancelled the transaction. Please try again when ready.';
      } else if (errorMessage.includes('insufficient funds') || errorMessage.includes('insufficient')) {
        userMessage = 'You don\'t have enough tokens or ETH to complete this transaction. Please check your wallet balance.';
      } else if (errorMessage.includes('allowance') || errorMessage.includes('approval')) {
        userMessage = 'There was an issue with token approval. Please try again.';
      }
      
      // Add user-friendly error message to chat
      setMessages(prev => [...prev, {
        id: (Date.now() + 2).toString(),
        text: userMessage,
        sender: 'lexi',
        timestamp: new Date(),
      }]);
      setIsLoading(false);
    }
  }, [writeError]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !writeContractAsync || !address) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);
    setCurrentQuestion(userMessage);

    // Add user message
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      text: userMessage,
      sender: 'user',
      timestamp: new Date(),
    }]);

    try {
      // Step 1: Approve LEXI tokens if using dynamic fees and allowance is insufficient
      if (isDynamic && chatFee && parseFloat(chatFee) > 0) {
        const feeAmount = parseUnits(chatFee, 18);
        const approvalAmount = parseUnits('10000', 18);
        const neededAllowance = feeAmount;
        
        // Check if we have enough allowance
        const hasEnoughAllowance = currentAllowance && currentAllowance >= neededAllowance;
        
        if (!hasEnoughAllowance) {
          console.log('💰 Approving LEXI tokens for chat fee...');
          console.log('Current allowance:', currentAllowance?.toString() || '0');
          console.log('Required allowance:', neededAllowance.toString());
          
          try {
            await approveAsync({
              args: [contracts.LEXI_CORE, approvalAmount],
            });
            console.log('✅ Token approval submitted, waiting for confirmation...');
            
            // Wait a bit for approval to propagate on-chain
            await new Promise(resolve => setTimeout(resolve, 3000));
            console.log('✅ Proceeding with askLexi call...');
          } catch (approvalError: any) {
            console.error('Error approving tokens:', approvalError);
            if (!approvalError.message?.includes('User rejected')) {
              setMessages(prev => [...prev, {
                id: (Date.now() + 1).toString(),
                text: `❌ Error approving tokens: ${approvalError.message || 'Failed to approve LEXI tokens'}`,
                sender: 'lexi',
                timestamp: new Date(),
              }]);
            }
            setIsLoading(false);
            return;
          }
        } else {
          console.log('✅ Sufficient LEXI token allowance already exists');
        }
      }

      // Step 2: Call askLexi
      console.log('🤖 Calling askLexi...');
      await writeContractAsync({
        args: [userMessage],
      });
    } catch (error: any) {
      console.error('Error sending message:', error);
      if (!error.message?.includes('User rejected')) {
        setMessages(prev => [...prev, {
          id: (Date.now() + 1).toString(),
          text: `❌ Error: ${error.message || 'Failed to send transaction'}`,
          sender: 'lexi',
          timestamp: new Date(),
        }]);
      }
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={3}
        className="retro-terminal retro-terminal-bg"
        sx={{
          height: '80vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#000',
          border: '2px solid #00ff00',
          position: 'relative',
        }}
      >
        {/* Scanline effect */}
        <Box className="retro-terminal-scanlines" sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 10 }} />
        
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: '2px solid #00ff00', position: 'relative', zIndex: 2 }}>
          <Typography variant="h4" className="retro-terminal retro-terminal-text" sx={{ color: '#00ff00', fontFamily: "'Courier Prime', 'Courier New', monospace", fontSize: '24px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', textShadow: '0 0 5px #00ff00' }}>
            {'>'} LEXI ORACLE SYSTEM [ONLINE]
          </Typography>
          <Typography variant="body2" className="retro-terminal retro-terminal-text" sx={{ mt: 1, color: '#00ff00', opacity: 0.8, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
            {'>'} BASE MAINNET [CHAIN ID: 8453] | STATUS: READY
          </Typography>
        </Box>

        {/* Connection Status */}
        {!isConnected && (
          <Alert severity="info" sx={{ m: 2 }}>
            Please connect your wallet to interact with LEXI Oracle
          </Alert>
        )}

        {/* Messages Area */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                mb: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: message.sender === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  maxWidth: '80%',
                  backgroundColor: message.sender === 'user' 
                    ? 'rgba(0, 100, 255, 0.1)' 
                    : 'rgba(0, 255, 0, 0.05)',
                  border: 1,
                  borderColor: message.sender === 'user' ? 'primary.main' : 'success.main',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    label={message.sender === 'user' ? 'You' : 'LEXI'}
                    size="small"
                    color={message.sender === 'user' ? 'primary' : 'success'}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {message.timestamp.toLocaleTimeString()}
                  </Typography>
                </Box>
                
                <Typography 
                  variant="body1" 
                  className="retro-terminal retro-terminal-text"
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: "'Courier Prime', 'Courier New', monospace",
                    fontSize: '14px',
                    lineHeight: '1.3',
                    letterSpacing: '0.5px',
                    textTransform: 'none',
                  }}
                >
                  {message.text}
                </Typography>

                {message.txHash && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      <strong>Transaction:</strong>{' '}
                      <Link
                        href={getTxUrl(effectiveChainId, message.txHash)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'success.main' }}
                      >
                        {message.txHash.slice(0, 10)}...{message.txHash.slice(-8)}
                      </Link>
                    </Typography>
                    {message.gasUsed && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Gas Used: {message.gasUsed.toString()}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
          ))}
          
          {(isPending || isConfirming) && (
            <Box sx={{ mb: 3 }}>
              <Paper elevation={2} sx={{ p: 2, backgroundColor: 'rgba(0, 255, 0, 0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <CircularProgress size={20} color="success" />
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    {isPending && '⏳ Waiting for wallet confirmation...'}
                    {isConfirming && '⛓️ Transaction confirming on-chain...'}
                  </Typography>
                </Box>
                <LinearProgress color="success" sx={{ mt: 1 }} />
              </Paper>
            </Box>
          )}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{ p: 3, borderTop: '2px solid #00ff00', backgroundColor: '#000', position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="ENTER QUERY..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={!isConnected || isLoading || isPending || isConfirming}
              multiline
              maxRows={4}
              className="retro-terminal"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: "'Courier Prime', 'Courier New', monospace",
                  fontSize: '14px',
                  color: '#00ff00',
                  backgroundColor: '#000',
                  '& fieldset': {
                    borderColor: '#00ff00',
                    borderWidth: '2px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#00ff00',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00ff00',
                  },
                },
                '& input': {
                  color: '#00ff00',
                  fontFamily: "'Courier Prime', 'Courier New', monospace",
                  fontSize: '14px',
                  textTransform: 'uppercase',
                },
                '& textarea': {
                  color: '#00ff00',
                  fontFamily: "'Courier Prime', 'Courier New', monospace",
                  fontSize: '14px',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || !isConnected || isLoading || isPending || isConfirming}
              className="retro-terminal"
              sx={{ 
                minWidth: 120, 
                height: 56,
                fontFamily: "'Courier Prime', 'Courier New', monospace",
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '2px',
                backgroundColor: '#000',
                color: '#00ff00',
                border: '2px solid #00ff00',
                '&:hover': {
                  backgroundColor: '#00ff00',
                  color: '#000',
                },
                '&:disabled': {
                  backgroundColor: '#000',
                  color: '#004400',
                  borderColor: '#004400',
                },
              }}
            >
              {isPending || isConfirming ? <CircularProgress size={24} sx={{ color: '#00ff00' }} /> : 'SEND'}
            </Button>
          </Box>
          
          <Typography variant="caption" className="retro-terminal retro-terminal-text" sx={{ color: '#00ff00', display: 'block', mt: 1, fontSize: '11px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px' }}>
            {'>'} QUERY EXAMPLES: "WHO ARE YOU?" | "WHAT IS LIVING ARCADE?" | "TELL ME ABOUT YOURSELF"
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}

// Wrapper component with mounting check
const OracleInterfaceV2: React.FC = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <LoadingOracle />;
  }

  return <OracleInterfaceInner />;
};

export default OracleInterfaceV2;
